import { TellescopeModelFromName, usePermissionQuery } from "@tellescope/tanstack-query";
import { Badge, Button, cn, Popover, PopoverContent, PopoverTrigger } from "@tellescope/ui";
import { formatPhone } from "@tellescope/utils";
import { Clock, Ellipsis, FileText, MapPin, Phone, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface LocationCardProps {
  location: TellescopeModelFromName<"appointment_locations">;
  onClick?: (() => void) | { to: string; replace?: boolean };
  onDelete?: () => void;
}

export const LocationCard = ({ location, onClick, onDelete }: LocationCardProps) => {
  const { t } = useTranslation("calendar");
  const Component = typeof onClick === "function" ? "button" : typeof onClick === "object" ? Link : "div";
  const addressString = useMemo(() => {
    const segments: string[] = [];
    if (location.address) {
      segments.push(location.address);
    }
    if (location.city) {
      segments.push(location.city);
    }
    if (location.state || location.zipCode) {
      segments.push(`${location.state ?? ""} ${location.zipCode}`.trim());
    }
    return segments.join(", ");
  }, [location.address, location.city, location.state, location.zipCode]);

  const {
    delete: { access: deleteAccess },
  } = usePermissionQuery({
    model: "appointment_locations",
    actions: ["delete"],
    record: location,
  });
  const canDelete = deleteAccess && !!onDelete;
  const hasNoMetadata = !addressString && !location.phone && !location.timezone && !location.instructions;

  return (
    <div
      data-testid={`location-card-${location.id}`}
      className={cn(
        "border rounded-md flex flex-col group relative",
        Component !== "div" && "transition-all hover:shadow-sm"
      )}
    >
      <Component
        className="flex flex-col grow"
        to={typeof onClick === "object" ? onClick.to : ""}
        replace={typeof onClick === "object" ? onClick.replace : undefined}
        onClick={typeof onClick === "function" ? onClick : undefined}
      >
        <div className="p-3 space-y-2 grow">
          <p className="text-sm">{location.title}</p>
          <div className="text-muted-foreground space-y-2">
            {!!addressString && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
                <MapPin size={15} />
                {addressString}
              </span>
            )}
            {!!location.phone && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
                <Phone size={15} />
                {formatPhone(location.phone)}
              </span>
            )}
            {!!location.timezone && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
                <Clock size={15} />
                {location.timezone}
              </span>
            )}
            {!!location.instructions && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
                <FileText size={15} />
                {location.instructions}
              </span>
            )}
            {hasNoMetadata && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2 italic">
                {t("No information to display")}
              </span>
            )}
          </div>
        </div>
        <div className="px-4 bg-muted min-h-9 flex gap-2 items-center rounded-b-md">
          {location.tags?.length ? (
            location.tags?.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="bg-white rounded-full font-light text-muted-foreground h-min"
              >
                {tag}
              </Badge>
            ))
          ) : (
            <p className="text-xs text-muted-foreground font-light italic">{t("No tags")}</p>
          )}
        </div>
      </Component>
      {canDelete && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              data-testid={`location-card-menu-${location.id}`}
              variant="ghost"
              size="sm-icon"
              className="invisible pointer-events-none group-hover:visible data-[state=open]:visible group-hover:pointer-events-auto data-[state=open]:pointer-events-auto absolute top-2 right-2"
            >
              <Ellipsis />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-1 w-min" align="end">
            <Button
              data-testid={`location-card-delete-${location.id}`}
              variant="ghost"
              className="text-destructive"
              onClick={onDelete}
            >
              <Trash2 />
              {t("Delete")}
            </Button>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
